import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Loader2 } from 'lucide-react';
import { setCredentials, fetchCurrentUser } from '../store/slices/authSlice';

/**
 * AuthCallback — landing page for /auth/callback#token=…
 *
 * The backend redirects here after Google OAuth completes. We:
 *   1. Pull the JWT out of the URL fragment.
 *   2. Drop it into Redux via setCredentials.
 *   3. Strip the fragment from the address bar (history.replaceState) so
 *      the token doesn't end up in browser history or referrer headers.
 *   4. Fetch /me to populate the user object.
 *   5. Redirect home.
 *
 * If no token is present we bounce to /signin with an error.
 */
export default function AuthCallbackPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const hash = window.location.hash || '';
    const params = new URLSearchParams(hash.replace(/^#/, ''));
    const token = params.get('token');

    if (!token) {
      navigate('/signin?error=oauth_failed', { replace: true });
      return;
    }

    dispatch(setCredentials({ token }));
    // Strip token from URL — never let it survive in history
    window.history.replaceState({}, document.title, '/auth/callback');

    dispatch(fetchCurrentUser()).finally(() => {
      navigate('/', { replace: true });
    });
  }, [dispatch, navigate]);

  return (
    <main className="min-h-screen bg-cream-100 pt-24 md:pt-32 flex items-start justify-center">
      <div className="text-center mt-16">
        <Loader2 size={28} className="text-gold animate-spin mx-auto mb-5" />
        <h1 className="font-serif text-2xl text-charcoal">Signing you in…</h1>
        <p className="font-sans text-sm text-warm-stone mt-2">
          One moment while we finish setting up your session.
        </p>
      </div>
    </main>
  );
}
